import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import { sendEmail } from '../../api/ses';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';

// --- HELPER COMPONENTS ---
const FadeInSection = ({ children, className = '' }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      });
    }, { threshold: 0.1 });

    if (domRef.current) observer.observe(domRef.current);
    return () => {
      if (domRef.current) observer.unobserve(domRef.current);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${className}`}
    >
      {children}
    </div>
  );
};

const FAQItem = ({ q, a, isOpen, onClick }) => (
  <div className="border border-tan rounded-md mb-4 bg-white overflow-hidden shadow-sm">
    <button
      className="w-full text-left px-6 py-4 font-bold text-dark-green flex justify-between items-center hover:bg-cream/50 transition-colors"
      onClick={onClick}
    >
      <span className="pr-4">{q}</span>
      <SafeIcon name={isOpen ? "ChevronUp" : "ChevronDown"} className="text-forest-green shrink-0" />
    </button>
    <div className={`px-6 text-muted overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-4 opacity-100' : 'max-h-0 opacity-0'}`}>
      {a}
    </div>
  </div>
);

// --- MAIN COMPONENT ---
export default function HomePage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  
  // Waitlist State
  const [waitlistForm, setWaitlistForm] = useState({ name: '', email: '' });
  const [submittingWaitlist, setSubmittingWaitlist] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  
  // UI State
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('settings_gwacamp');
        const data = Array.isArray(res) ? res : res.data || [];
        const map = {};
        data.forEach(s => map[s.key || s.setting_key] = s.value);
        setSettings(map);
      } catch (e) {
        console.error("Failed to load settings", e);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchSettings();
  }, []);

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  const loginUrl = 'https://gospelwiseauthor.app/login?redirect=https://camp.faibowrimo.com/#/login';
  
  // Registration Logic
  // Exclusively obey the database settings. No date fallbacks that can accidentally close registration.
  let isRegistrationOpen = true; 
  if (settings.registration_open !== undefined) {
    isRegistrationOpen = String(settings.registration_open).toLowerCase() !== 'false';
  } else if (settings.enable_registration !== undefined) {
    isRegistrationOpen = String(settings.enable_registration).toLowerCase() !== 'false';
  }

  const showWaitlist = !loadingConfig && !isRegistrationOpen;
  const showRegisterButton = !loadingConfig && isRegistrationOpen;
  const registerUrl = 'https://camp.gospelwiseauthor.com/#/start';
  const registerText = 'Register Free';

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name || !waitlistForm.email) return;
    setSubmittingWaitlist(true);
    try {
      await api.post('waitlist_gwacamp', {
        id: crypto.randomUUID(),
        name: waitlistForm.name,
        email: waitlistForm.email,
        owner_id: 'admin'
      });
      const emailHtml = `
        <div style="font-family: sans-serif;max-width: 600px;margin: 0 auto;color: #333;">
          <h2 style="color: #2D5016;">You're on the list!</h2>
          <p>Hi ${waitlistForm.name.split(' ')[0]},</p>
          <p>Thanks for joining the waitlist for GospelWise Author Camp. We'll let you know as soon as registration opens for the next cohort!</p>
          <p>Best,<br>GospelWise Author Team</p>
        </div>
      `;
      await sendEmail(waitlistForm.email, "You're on the waitlist!", emailHtml);
      setWaitlistSuccess(true);
      setWaitlistForm({ name: '', email: '' });
    } catch (err) {
      console.error("Waitlist error", err);
    } finally {
      setSubmittingWaitlist(false);
    }
  };

  const WaitlistUI = () => (
    <div className="bg-cream/10 backdrop-blur-md p-6 rounded-xl border border-tan/30 shadow-2xl max-w-md w-full mx-auto md:mx-0 text-left">
      {waitlistSuccess ? (
        <div className="text-center py-4 space-y-4">
          <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center text-white mb-2 shadow-sm">
            <SafeIcon name="Check" className="text-3xl" />
          </div>
          <h3 className="text-2xl font-bold text-white">You're on the list!</h3>
          <p className="text-cream text-sm">Check your email for a confirmation. We'll be in touch as soon as dates are announced.</p>
        </div>
      ) : (
        <form onSubmit={handleWaitlistSubmit} className="space-y-4">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-white">Camp is currently closed.</h3>
            <p className="text-sm text-cream mt-1">Join the waitlist to be notified when registration opens for the next cohort!</p>
          </div>
          <Input 
            label="First Name" 
            required
            value={waitlistForm.name} 
            onChange={e => setWaitlistForm({...waitlistForm, name: e.target.value})}
            className="bg-white text-dark-green border-transparent"
          />
          <Input 
            type="email" 
            label="Email Address" 
            required
            value={waitlistForm.email} 
            onChange={e => setWaitlistForm({...waitlistForm, email: e.target.value})}
            className="bg-white text-dark-green border-transparent"
          />
          <Button type="submit" className="w-full text-lg py-3 shadow-md mt-2 bg-amber hover:bg-orange-600 text-dark-green font-bold border-none" disabled={submittingWaitlist}>
            {submittingWaitlist ? 'Joining...' : 'Join the Waitlist'}
          </Button>
          <div className="text-center pt-2">
            <a href={loginUrl} className="text-sm text-cream hover:text-white hover:underline flex items-center justify-center gap-1 transition-colors">
              <SafeIcon name="Lock" className="text-xs" /> Already have an account? Log in.
            </a>
          </div>
        </form>
      )}
    </div>
  );

  const faqs = [
    {
      q: "Is camp really free?",
      a: "Yes — completely free to register and attend. The All-Access Pass ($9 early bird / $19 regular) is an optional upgrade that unlocks additional features and lets you keep everything you build. But the core camp experience — five days of training, the Compass workbook, the live Meetn sessions, and the community — is free."
    },
    {
      q: "I haven't done GospelWise Author Camp before. Can I still come to camp?",
      a: "Absolutely. GospelWise Author Camp stands on its own. You don't need any prior GospelWise Author Camp experience. If you're a Christian author at any stage — idea, draft, finished manuscript, or already published — camp is built for you."
    },
    {
      q: "I write nonfiction. Is this for fiction authors only?",
      a: "Not at all. The GospelWise Author framework applies to every Christian author regardless of genre — novelists, memoirists, Bible study writers, devotional authors, and everything in between. Your story isn't just your novel. It's your calling."
    },
    {
      q: "How much time does camp take each day?",
      a: "About 50 minutes on a typical day — 20-25 minutes for the Morning Assembly video, 30 minutes for the Midday Mess Hall Meetn (optional), and 20–30 minutes for your Cabin Call workbook. The Evening Campfire is async in the Facebook group and takes as long as you want. You can participate fully around work, family, and summer life."
    },
    {
      q: "What's the difference between Free and All-Access?",
      a: "Free gives you the full five-day camp experience — videos, Meetn sessions, the Compass workbook, and the community. All-Access adds the things that make the experience more personal and lasting: a self-letter writing studio, a bunkmate match, daily personal notes from Jeremy, video replays through August, and your Compass saved permanently in your profile."
    },
    {
      q: "Will the videos be available after camp ends?",
      a: "Free campers have access to videos during camp week and through Saturday July 19. All-Access pass holders keep replay access through August 31, 2026."
    }
  ];

  return (
    <div className="min-h-screen bg-cream font-poppins text-dark-green flex flex-col overflow-x-hidden">
      {/* NAVIGATION BAR */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md border-b border-amber' : 'bg-white border-b border-amber'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-forest-green rounded-full flex items-center justify-center text-cream">
                <SafeIcon name="Compass" className="text-xl" />
              </div>
              <span className="font-bold text-xl text-forest-green tracking-tight">GospelWise Author Camp</span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4">
              {showRegisterButton && (
                <a href={registerUrl}>
                  <Button className="bg-forest-green hover:bg-dark-green text-white font-semibold px-6 py-2.5 rounded-lg shadow-sm">
                    {registerText}
                  </Button>
                </a>
              )}
              <a href={loginUrl}>
                <Button variant="outline" className="border-2 border-amber text-amber hover:bg-amber hover:text-white font-semibold px-6 py-2.5 rounded-lg">
                  Log In
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-forest-green hover:text-dark-green p-2"
              >
                <SafeIcon name={isMobileMenuOpen ? "X" : "Menu"} className="text-3xl" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-amber px-4 pt-2 pb-6 space-y-3 shadow-lg absolute w-full">
            {showRegisterButton && (
              <a href={registerUrl} className="block w-full">
                <Button className="w-full bg-forest-green text-white py-3 text-lg">{registerText}</Button>
              </a>
            )}
            <a href={loginUrl} className="block w-full">
              <Button variant="outline" className="w-full border-2 border-amber text-amber py-3 text-lg">Log In</Button>
            </a>
          </div>
        )}
      </nav>

      {/* SECTION 1: HERO */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-gradient-to-br from-[#2D5016] to-[#1A2E0A] overflow-hidden">
        {/* Subtle background texture/glow */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-amber rounded-full mix-blend-overlay filter blur-[150px] opacity-20 transform translate-x-1/3 -translate-y-1/4 pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Right Image (Logo) & Slogan & Video */}
            <FadeInSection className="order-1 md:order-2 flex flex-col justify-center items-center w-full">
              <img src="https://www.crossandpen.com/wp-content/uploads/2026/08/gwacamp_logo_main.png" alt="GospelWise Author Camp Logo" className="w-full max-w-2xl lg:max-w-3xl h-auto object-contain drop-shadow-2xl" />
              <h2 className="mt-4 md:mt-6 mb-6 md:mb-8 text-2xl md:text-3xl lg:text-4xl font-semibold italic text-amber text-center">
                Your Story. God's Glory.
              </h2>
              
              {/* Video Embed */}
              <div className="w-full max-w-xl aspect-video rounded-xl overflow-hidden shadow-2xl border border-amber/30 mt-2">
                <iframe 
                  src="https://play.gumlet.io/embed/6a29ceaafb9f7c8819fa48c3?background=false&autoplay=false&loop=false&disable_player_controls=false" 
                  title="GospelWise Author Camp Welcome Video"
                  className="w-full h-full border-none"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
            </FadeInSection>

            {/* Left Content */}
            <FadeInSection className="order-2 md:order-1 space-y-8 text-center md:text-left">
              <div className="inline-block px-4 py-1.5 rounded-full bg-amber/20 border border-amber/50 text-amber text-s font-bold tracking-widest uppercase shadow-sm">FREE 5-DAY CHALLENGE</div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Five days to map your book, find your people, and take your next faithful step.
              </h1>
              <p className="text-lg md:text-xl text-cream/90 leading-relaxed max-w-2xl mx-auto md:mx-0 font-light">
                GospelWise Author Camp is a free week-long author challenge for Christian writers. Each day you'll build one section of your GospelWise Author Compass — a personalized plan for finishing and launching your book.
              </p>
              
              <div className="pt-4 min-h-[120px]">
                {loadingConfig ? (
                  <div className="flex items-center justify-center md:justify-start text-cream h-full">
                    <SafeIcon name="Loader" className="animate-spin text-2xl mr-3" /> Loading camp status...
                  </div>
                ) : showWaitlist ? (
                  <WaitlistUI />
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                      <a href={registerUrl} className="w-full sm:w-auto">
                        <Button className="w-full sm:w-auto bg-amber hover:bg-orange-600 text-dark-green font-bold text-lg px-8 py-4 rounded-lg shadow-lg border-none">
                          Register Free →
                        </Button>
                      </a>
                      <a href={loginUrl} className="w-full sm:w-auto">
                        <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-dark-green font-bold text-lg px-8 py-4 rounded-lg">
                          I Already Have an Account
                        </Button>
                      </a>
                    </div>
                    <p className="text-sm text-cream/70 italic text-center md:text-left">
                      Free to join. No credit card. All-Access upgrade available.
                    </p>
                  </div>
                )}
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* SECTION 2: WHAT IS CAMP */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-forest-green mb-4">What Happens at Camp?</h2>
            <p className="text-xl text-muted">One week. Four personas. One compass that points you forward.</p>
          </FadeInSection>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:gap-8 pb-8 md:pb-0">
            {[
              { day: "DAY 1 —  Calling", persona: "The Christian", topic: "Mindset + Calling", desc: "Reclaim your calling and name why this book matters to God and to your reader." },
              { day: "DAY 2 —  Writing", persona: "The Creator", topic: "Writing + Craft", desc: "Get honest about where your manuscript is and what your next faithful writing move is." },
              { day: "DAY 3 —  Publishing", persona: "The Caretaker", topic: "Publishing + Business", desc: "Demystify publishing and choose the path that fits your book and your season." },
              { day: "DAY 4 —  Marketing", persona: "The Connector", topic: "Marketing + Sales", desc: "Name your one reader and build your minimum faithful marketing presence." },
              { day: "DAY 5 — Direction", persona: "The Compass", topic: "Review + Export", desc: "Bring your four sections together into your complete GospelWise Author Compass." }
            ].map((d, i) => (
              <FadeInSection 
                key={i} 
                className={`w-full bg-cream rounded-xl p-8 border border-tan/50 shadow-sm hover:shadow-md hover:bg-tan/20 transition-all border-t-4 border-t-forest-green flex flex-col md:col-span-2 ${i === 3 ? 'md:col-start-2' : i === 4 ? 'md:col-start-4' : ''}`}
              >
                <div className="text-xs font-bold text-forest-green tracking-widest uppercase mb-4">{d.day}</div>
                <h3 className="text-2xl font-bold text-gwa-blue mb-1">{d.persona}</h3>
                <div className="text-sm font-bold text-amber uppercase tracking-wide mb-4">{d.topic}</div>
                <p className="text-muted leading-relaxed flex-1">{d.desc}</p>
              </FadeInSection>
            ))}
          </div>
          <FadeInSection className="text-center mt-12">
            <p className="text-muted italic text-lg">Each day builds on the last. By Day 5 you hold a complete plan — not just inspiration.</p>
          </FadeInSection>
        </div>
      </section>

      {/* SECTION 3: THE COMPASS DELIVERABLE */}
      <section className="py-24 bg-cream border-y border-tan">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            {/* Left: Mockup */}
            <FadeInSection className="order-2 md:order-1 flex justify-center">
              <div className="relative w-full max-w-md aspect-[8.5/11] bg-[#F8F1E5] border-2 border-amber rounded-r-3xl rounded-l-md shadow-2xl flex flex-col items-center justify-center p-12 text-center transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-tan/50"></div>
                <div className="absolute left-6 top-0 bottom-0 w-px bg-tan/50"></div>
                <SafeIcon name="Compass" className="text-6xl text-forest-green mb-6" />
                <h3 className="text-3xl font-bold text-forest-green uppercase tracking-wide leading-snug mb-8 border-y-2 border-forest-green py-6">
                  The GospelWise<br/>Author Compass
                </h3>
                <div className="mt-auto flex items-center justify-center gap-3 text-amber font-bold tracking-widest text-sm">
                  <span>FAITH</span>
                  <span className="text-[8px]">✦</span>
                  <span>FOCUS</span>
                  <span className="text-[8px]">✦</span>
                  <span>FINISH</span>
                </div>
              </div>
            </FadeInSection>
            
            {/* Right: Copy */}
            <FadeInSection className="order-1 md:order-2 space-y-6">
              <div className="text-sm font-bold text-amber tracking-widest uppercase">YOUR CAMP DELIVERABLE</div>
              <h2 className="text-3xl md:text-4xl font-bold text-forest-green leading-tight">
                Leave camp with your GospelWise Author Compass
              </h2>
              <p className="text-lg text-muted leading-relaxed">
                Every Cabin Call session, you'll add one section to your Author Compass — a personalized document that tells you exactly where you are in your author journey and what your next faithful step is in each area.
              </p>
              <ul className="space-y-4 pt-2 pb-6">
                {[
                  "Your calling statement — why this book, why you, why now",
                  "Your writing plan — where your manuscript is and how to move it forward",
                  "Your publishing path — which route fits your book and your season",
                  "Your marketing clarity — who your reader is and your first move to reach them"
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="mt-1 bg-forest-green/10 p-1 rounded-full shrink-0">
                      <SafeIcon name="Check" className="text-forest-green text-sm" />
                    </div>
                    <span className="text-dark-green font-medium">{bullet}</span>
                  </li>
                ))}
              </ul>
              {showRegisterButton && (
                <a href={registerUrl} className="inline-block">
                  <Button className="bg-forest-green hover:bg-dark-green text-white font-bold text-lg px-8 py-4 rounded-lg shadow-md">
                    Register Free and Build Your Compass →
                  </Button>
                </a>
              )}
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* SECTION 4: THE DAILY SCHEDULE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-forest-green">What a Day at Camp Looks Like</h2>
          </FadeInSection>
          <div className="flex flex-col md:flex-row gap-6 mb-12">
            {[
              { emoji: "☀️", time: "7:00am", title: "Morning Assembly", desc: "A 20–25 minute video training drops in the app. Watch it on your schedule." },
              { emoji: "🥪", time: "1:00pm", title: "Midday Mess Hall", desc: "Join a live Meetn check-in at 1pm. Share what you built yesterday. Preview today." },
              { emoji: "📓", time: "2:00pm", title: "Cabin Call", desc: "Open your Compass workbook at 2pm. Complete that day's section. Auto-saves as you write." },
              { emoji: "🔥", time: "Evening", title: "Evening Campfire", desc: "A reflection prompt drops in the Facebook group. Share, encourage, connect." }
            ].map((block, i) => (
              <FadeInSection key={i} className="flex-1 bg-cream rounded-xl p-8 border border-tan border-b-4 border-b-amber text-center hover:-translate-y-1 transition-transform">
                <div className="text-5xl mb-4">{block.emoji}</div>
                <div className="text-amber font-bold text-lg mb-1">{block.time}</div>
                <h3 className="text-xl font-bold text-forest-green mb-3">{block.title}</h3>
                <p className="text-muted text-sm leading-relaxed">{block.desc}</p>
              </FadeInSection>
            ))}
          </div>
          <FadeInSection className="max-w-3xl mx-auto bg-tan/20 border-l-4 border-forest-green p-6 rounded-r-lg text-center">
            <p className="text-dark-green font-medium text-lg">
              About 50 minutes a day — with the option to go deeper. You can do this around work, family, and summer life.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* SECTION 5: FREE VS ALL-ACCESS */}
      <section className="py-24 bg-cream border-t border-tan">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-forest-green max-w-3xl mx-auto leading-tight">
              Camp is Free. All-Access is for Those Who Want to Keep Everything.
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Free Tier */}
            <FadeInSection className="bg-white rounded-2xl p-8 md:p-10 border border-tan shadow-sm flex flex-col">
              <h3 className="text-2xl font-bold text-dark-green mb-8 text-center">Free Camp</h3>
              <ul className="space-y-5 flex-1 mb-8">
                {[
                  "Access to all 5 Morning Assembly videos (live only)",
                  "Daily Midday Mess Hall Meetn",
                  "GospelWise Author Compass workbook (write + export once)",
                  "Evening Campfire community prompts",
                  "GWA Fellowship community access"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <SafeIcon name="Check" className="text-forest-green text-xl shrink-0 mt-0.5" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
              {showRegisterButton && (
                <div className="text-center mt-auto">
                  <a href={registerUrl} className="text-muted hover:text-forest-green font-bold text-lg underline underline-offset-4 transition-colors">
                    Register Free →
                  </a>
                </div>
              )}
            </FadeInSection>

            {/* All-Access Tier */}
            <FadeInSection className="bg-forest-green rounded-2xl p-8 md:p-10 shadow-xl relative flex flex-col transform md:-translate-y-4">
              <div className="absolute top-0 right-0 bg-amber text-dark-green text-xs font-bold px-4 py-1.5 uppercase tracking-widest rounded-bl-lg rounded-tr-2xl">
                MOST POPULAR
              </div>
              <h3 className="text-2xl font-bold text-white mb-8 text-center mt-2">All-Access Pass</h3>
              <ul className="space-y-5 flex-1 mb-8">
                <li className="flex items-start gap-3 border-b border-white/20 pb-4 mb-4">
                  <SafeIcon name="Plus" className="text-amber text-xl shrink-0 mt-0.5" />
                  <span className="text-white font-bold italic">Everything in Free, plus:</span>
                </li>
                {[
                  "Self-Letter Writing Studio — write yourself a letter for each morning",
                  "Bunkmate matching — paired with a fellow author in your genre",
                  "Cabin Counselor emails — personal daily notes from Jeremy",
                  "Video replay access through August 31",
                  "Permanent Compass archive in your GWA profile",
                  "Printable Camp Kit — designed PDF bundle",
                  "30-day post-camp Compass Check email sequence",
                  "GWA Christian Publishing Handbook",
                  "GWA Roadmap Course"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <SafeIcon name="Star" className="text-amber text-xl shrink-0 mt-0.5" />
                    <span className="text-cream/90">{item}</span>
                  </li>
                ))}
              </ul>
              {showRegisterButton && (
                <div className="text-center mt-auto pt-4">
                  <a href="https://camp.gospelwiseauthor.com/#/start?plan=allaccess" className="block w-full">
                    <Button className="w-full bg-amber hover:bg-orange-600 text-dark-green font-bold text-lg py-4 rounded-xl shadow-lg border-none">
                      Get All-Access — $9 Early Bird →
                    </Button>
                  </a>
                  <p className="text-cream/60 text-xs mt-3">Price increases to $19</p>
                </div>
              )}
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* SECTION 6: ABOUT JEREMY */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <FadeInSection className="md:col-span-4 flex justify-center">
              <div className="w-48 h-48 md:w-full md:aspect-square rounded-full overflow-hidden border-4 border-amber shadow-lg relative">
                <img src="https://www.faibowrimo.com/wp-content/uploads/2026/05/jb-1_rec.jpg?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=140" alt="Jeremy Bouma" className="w-full h-full object-cover" />
              </div>
            </FadeInSection>
            <FadeInSection className="md:col-span-8 text-center md:text-left space-y-4">
              <div className="text-sm font-bold text-amber tracking-widest uppercase">YOUR CAMP DIRECTOR</div>
              <h2 className="text-3xl md:text-4xl font-bold text-forest-green">Jeremy Bouma</h2>
              <p className="text-lg text-muted leading-relaxed">
                I've published over 70 books — fiction, nonfiction, a study Bible, audiobooks — as an indie author through my company EmmausWay Media Group. I know what it takes to move a manuscript from calling to published. I also know what it feels like when the book sits unfinished because life got in the way. GospelWise Author Camp exists because your story deserves to be told.
              </p>
              <p className="text-xl text-amber italic font-medium pt-2">
                "Wield the pen for the sake of the cross."
              </p>
              <div className="pt-4">
                <a href="https://gospelwiseauthor.com" target="_blank" rel="noopener noreferrer" className="text-forest-green font-bold hover:underline inline-flex items-center gap-1">
                  Learn more at gospelwiseauthor.com <SafeIcon name="ArrowRight" className="text-sm" />
                </a>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* SECTION 7: TESTIMONIALS */}
      <section className="py-24 bg-cream border-t border-tan">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-forest-green">What GospelWise Author Camp Authors Say</h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              { 
                quote: "Jeremy, you lit a fire in me — The few days I have been a part of this group has helped me tremendously. For sometime I have searched for a place to belong as a writer and a Christian.",
                author: "Lefty"
              },
              {
                quote: "I'm kind of in shock at how many days I've been able to write in a row and to average more than a 1000 words. It's really exciting! This group and the app has been really motivating for me.",
                author: "Amanda"
              },
              {
                quote: "Well organized with daily prompts and encouragement, I highly recommend this for anyone who writes.",
                author: "Rhonda"
              }
            ].map((t, i) => (
              <FadeInSection key={i} className="bg-cream rounded-xl p-8 border border-tan border-t-4 border-t-amber shadow-sm flex flex-col">
                <div className="flex text-amber mb-4">
                  {[1, 2, 3, 4, 5].map(s => <SafeIcon key={s} name="Star" className="text-lg fill-current" />)}
                </div>
                <p className="text-muted italic leading-relaxed mb-6 flex-1">"{t.quote}"</p>
                <div>
                  <div className="font-bold text-forest-green">{t.author}</div>
                  <div className="text-sm text-muted">Christian author • GospelWise Author Camp participant</div>
                </div>
              </FadeInSection>
            ))}
          </div>
          <FadeInSection className="text-center">
            <p className="text-sm text-muted italic">Testimonials from the past GospelWise Author Camp rounds. GospelWise Author Camp launches July 2026.</p>
          </FadeInSection>
        </div>
      </section>

      {/* SECTION 8: FAQ */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-forest-green">Questions About Camp</h2>
          </FadeInSection>
          <FadeInSection className="space-y-4">
            {faqs.map((faq, i) => (
              <FAQItem 
                key={i} 
                q={faq.q} 
                a={faq.a} 
                isOpen={openFaqIndex === i} 
                onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)} 
              />
            ))}
          </FadeInSection>
        </div>
      </section>

      {/* SECTION 9: FINAL CTA */}
      <section className="py-24 bg-forest-green text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeInSection className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
              Your book is waiting. Your Compass is ready to be built.
            </h2>
            <p className="text-xl text-cream/90 font-light">
              GospelWise Author Camp · July 13–17, 2026 · Free to join
            </p>
            <div className="pt-8 flex justify-center min-h-[120px]">
              {loadingConfig ? (
                <div className="text-cream flex items-center h-full"><SafeIcon name="Loader" className="animate-spin inline mr-2" /> Loading...</div>
              ) : showWaitlist ? (
                <div className="w-full max-w-md mx-auto">
                  <WaitlistUI />
                </div>
              ) : (
                <div className="space-y-4">
                  <a href={registerUrl} className="inline-block w-full sm:w-auto">
                    <Button className="w-full sm:w-auto bg-amber hover:bg-orange-600 text-dark-green font-bold text-xl px-12 py-5 rounded-xl shadow-xl border-none">
                      Register Free →
                    </Button>
                  </a>
                  <p className="text-sm text-cream/70">
                    All-Access Pass available for $9 early bird · Price increases after Monday July 13
                  </p>
                </div>
              )}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* SECTION 10: FOOTER */}
      <footer className="bg-[#1A2E0A] border-t-2 border-amber pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center md:text-left mb-12">
            
            {/* Left */}
            <div className="space-y-2">
              <div className="flex items-center justify-center md:justify-start gap-2 text-white mb-4">
                <SafeIcon name="Compass" className="text-xl" />
                <span className="font-bold text-xl tracking-tight">GospelWise Author Camp</span>
              </div>
              <p className="text-cream text-sm">A GospelWise Author event.</p>
              <p className="text-muted text-xs pt-4">© 2026 CROSS + PEN</p>
            </div>

            {/* Center */}
            <div className="flex flex-col space-y-3 items-center md:items-start text-cream text-sm">
              <a href="#" className="hover:text-amber transition-colors">Home</a>
              {showRegisterButton && <a href={registerUrl} className="hover:text-amber transition-colors">Register</a>}
              <a href={loginUrl} className="hover:text-amber transition-colors">Log In / All-Access</a>
              <a href="https://gospelwiseauthor.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber transition-colors">gospelwiseauthor.com</a>
              <a href="https://crossandpen.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber transition-colors">crossandpen.com</a>
            </div>

            {/* Right */}
            <div className="flex flex-col items-center md:items-end justify-between">
              <p className="text-cream italic font-medium text-lg">
                "Wield the pen for the sake of the cross."
              </p>
              <div className="flex gap-4 mt-6 md:mt-0 text-cream">
                <a href="#" className="hover:text-amber transition-colors"><SafeIcon name="Facebook" className="text-xl" /></a>
                <a href="#" className="hover:text-amber transition-colors"><SafeIcon name="Instagram" className="text-xl" /></a>
              </div>
            </div>

          </div>
        </div>
      </footer>
    </div>
  );
}